import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Common, { FontAwesome, Ionicons } from 'src/components/Common';
import { colors } from 'src/constants/theme';
import useProfile from 'src/hooks/useProfile';
import useTheme from 'src/hooks/useTheme';
import { normalizeTicket } from 'src/utils/normalizer';
import routeNames from 'src/constants/routeNames';
import navigationService from 'src/utils/navigation';
import { formatDate } from 'src/utils/date';
import { useDispatchMapper } from 'src/hooks/actionHooks';
import { setActiveTicketAction } from 'src/redux/chat/chatActions';
import { useSelector } from 'react-redux';
import { removeTicketNotificationAction } from 'src/redux/notifications/actions';
import styles from './style';
import useTranslation from 'src/hooks/useTranslation';

const ServiceCard = ({ item }) => {
  const ticketData = useMemo(() => normalizeTicket(item), [item]);
  const saveActiveTicket = useDispatchMapper(setActiveTicketAction);
  const ticketNotifications = useSelector((store) => store.notifications.tickets);
  const removeTicketNotification = useDispatchMapper(removeTicketNotificationAction);
  const { role } = useProfile();
  const { isDarkMode, colors: themeColors } = useTheme();
  const { translate } = useTranslation();

  const onClickTicket = () => {
    saveActiveTicket(ticketData);
    removeTicketNotification(ticketData.id);
    navigationService.navigate(routeNames.CHAT, { ticket: ticketData });
  };

  return (
    <Common.Card style={[styles.card, { backgroundColor: themeColors.foreground }]}>
      <Common.TouchableOpacity activeOpacity={0.7} testID="service-card" onPress={onClickTicket}>
        <Common.View style={styles.cardHeader}>
          <Common.Text color={colors.blue}>{`Thread #${ticketData.number || ticketData.systemId}`}</Common.Text>
          <Common.Row alignItems="center">
            <Common.Text color={isDarkMode ? colors.grey500 : colors.grey600}>
              {formatDate(ticketData.updatedAt || ticketData.createdAt, 'MM/dd/yyyy')}
            </Common.Text>
            {ticketNotifications?.[ticketData?.id] && <Common.View style={styles.indicator} />}
          </Common.Row>
        </Common.View>
        <Common.Divider style={styles.divider} color={isDarkMode ? colors.grey800 : colors.grey20} />
        <Common.View transparent>
          <Common.Text size={15} numberOfLines={2}>
            {ticketData?.summary}
          </Common.Text>
          {role === 'admin' && (
            <Common.Row alignItems="center" style={{ marginTop: 10 }}>
              <FontAwesome name="user" size={25} color={colors.blue} style={{ marginRight: 5 }} />
              <Common.Row>
                <Common.Text bold>User:</Common.Text>
                <Common.Text style={styles.username}>{ticketData?.contactName}</Common.Text>
              </Common.Row>
            </Common.Row>
          )}
          <Common.Row alignItems="center" style={{ marginTop: 5 }}>
            <Ionicons name="md-notifications" size={26} color={colors.yellow} style={{ marginRight: 5 }} />
            <Common.Row>
              <Common.Text bold>{translate('common.text.status')}:</Common.Text>
              <Common.Text style={styles.status}>{ticketData?.statusObject?.name || ticketData?.status}</Common.Text>
            </Common.Row>
          </Common.Row>
        </Common.View>
      </Common.TouchableOpacity>
    </Common.Card>
  );
};

ServiceCard.propTypes = {
  item: PropTypes.object.isRequired,
};

export default React.memo(ServiceCard);
